import { useEffect } from "react";
import "./about.css";

const PAGE_TITLE = "About Apping Technology | Apping Sales OS";
const PAGE_DESCRIPTION = "Learn why Apping Technology is building Sales OS to connect market intelligence, account context, sales execution and better-informed next actions.";
const WALKTHROUGH = "/book-a-walkthrough";

const navigation = [
  ["Product", "/product"],
  ["Market Entry", "/market-entry"],
  ["Who It’s For", "/who-its-for"],
  ["Growth", "/growth"],
  ["Insights", "/insights"],
  ["About", "/about"],
];

const informationSources = [
  "Market research", "Company information", "Contact databases", "Spreadsheets", "CRM records",
  "Emails", "Meetings", "Documents", "Individual experience",
];

const workflow = ["Research", "Companies", "People", "Outreach", "Conversations", "New information"];
const practicalQuestions = [
  "What market should we investigate?",
  "Which companies deserve attention?",
  "Who matters inside the account?",
  "What happened when we engaged?",
  "What did we learn?",
  "What should happen next?",
];

const uncertainties = ["Market structure", "Relevant companies", "Decision-makers", "Commercial context", "Relationship development", "What the team should do first"];

const principles = [
  ["Context before volume", "More names are not automatically more useful."],
  ["Relevance before activity", "Sales effort should focus where there is a reason to investigate."],
  ["Intelligence should accumulate", "Useful learning should not disappear after the campaign or conversation ends."],
  ["Automation needs governance", "More automation should come with appropriate controls and oversight."],
  ["The next action matters", "Research and intelligence have value when they improve what the team does next."],
];

const directions = ["Market intelligence", "Account discovery", "Stakeholder context", "Sales execution", "Accumulated account intelligence"];

const explore = [
  ["Product", "See what Sales OS does.", "/product"],
  ["Market Entry", "See how Sales OS approaches unfamiliar markets.", "/market-entry"],
  ["Sales Intelligence", "See how account context can build over time.", "/sales-intelligence"],
  ["Who It’s For", "See which sales environments fit the product.", "/who-its-for"],
];

function ArrowLink({ href, children, primary = false }) {
  return <a className={primary ? "about-button" : "about-text-link"} href={href}>{children}<span aria-hidden="true">↗</span></a>;
}

function Header() {
  return (
    <header className="about-header">
      <a className="about-brand" href="/" aria-label="Apping Sales OS homepage"><img src="/apping-logo.png" alt="Apping Technology" width="985" height="999" /></a>
      <nav aria-label="Primary navigation">{navigation.map(([label, href]) => <a className={label === "About" ? "is-active" : ""} href={href} key={label}>{label}</a>)}</nav>
      <div className="about-header-actions"><span aria-label="English language active">EN <i aria-hidden="true">| 简</i></span><a className="about-button about-button-small" href={WALKTHROUGH}>Book a walkthrough</a></div>
    </header>
  );
}

function Intro({ eyebrow, title, children, id }) {
  return <div className="about-intro">{eyebrow ? <p className="about-eyebrow">{eyebrow}</p> : null}<h2 id={id}>{title}</h2>{children ? <div className="about-copy">{children}</div> : null}</div>;
}

function Footer() {
  return (
    <footer className="about-footer">
      <a className="about-footer-brand" href="/">Apping Sales OS</a>
<nav aria-label="Footer navigation"><a href="/product">Product</a><a href="/market-entry">Market Entry</a><a href="/sales-intelligence">Sales Intelligence</a><a href="/who-its-for">Who It’s For</a><a href="/growth">Apping Growth</a><a href="/insights">Insights</a><a href="/about">About</a><a href="/faq">FAQ</a><a href="/book-a-walkthrough">Book a Walkthrough</a><a href="/privacy">Privacy</a><a href="/terms">Terms</a><a href="/business-contact-data">Business Contact Data</a><a href="/privacy-request">Privacy Request</a></nav>
      <p>Apping Technology</p>
    </footer>
  );
}

export default function AboutPage() {
  useEffect(() => {
    const previousTitle = document.title;
    const description = document.querySelector('meta[name="description"]');
    const previousDescription = description?.getAttribute("content") || "";
    const schema = document.createElement("script");
    schema.type = "application/ld+json";
    schema.id = "about-structured-data";
    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        { "@type": "Organization", "@id": "https://appingtechnology.com/#organization", name: "Apping Technology", url: "https://appingtechnology.com", email: "info@appingtechnology.com" },
      ],
    });
    document.title = PAGE_TITLE;
    description?.setAttribute("content", PAGE_DESCRIPTION);
    document.head.appendChild(schema);
    document.documentElement.dataset.page = "about";
    window.__APPING_ABOUT_READY__ = true;
    return () => {
      document.title = previousTitle;
      description?.setAttribute("content", previousDescription);
      schema.remove();
      delete document.documentElement.dataset.page;
      delete window.__APPING_ABOUT_READY__;
    };
  }, []);

  return (
    <div className="about-page">
      <a className="about-skip" href="#about-main">Skip to content</a>
      <Header />
      <main id="about-main">
        <section className="about-hero" aria-labelledby="about-title">
          <div className="about-container about-hero-grid">
            <div className="about-hero-copy">
              <p className="about-eyebrow">About Apping</p>
              <h1 id="about-title">Building intelligence around how B2B sales actually works.</h1>
              <div className="about-hero-body">
                <p>We are developing Apping Sales OS to connect market research, account knowledge and sales decisions.</p>
              </div>
              <div className="about-actions"><ArrowLink href="/product" primary>Explore Sales OS</ArrowLink><ArrowLink href={WALKTHROUGH}>Book a walkthrough</ArrowLink></div>
            </div>
            <figure className="about-hero-team">
              <img src="/images/apping-team/apping-team-dinner.jpg" srcSet="/images/apping-team/apping-team-dinner-800.jpg 800w, /images/apping-team/apping-team-dinner.jpg 1920w" sizes="(max-width: 820px) calc(100vw - 34px), 48vw" alt="Apping Technology colleagues gathered around a table for a team dinner" width="1920" height="1080" fetchPriority="high" decoding="async" />
              <figcaption>Apping Technology team dinner</figcaption>
            </figure>
          </div>
        </section>

        <section className="about-section observation-section" aria-labelledby="observation-title">
          <div className="about-container observation-layout">
            <Intro title="Businesses do not have an information shortage." id="observation-title"><p>Sales teams already work with many sources of useful information.</p><p>The problem is that these pieces often remain separate.</p></Intro>
            <div className="information-field" aria-label="Information already used by sales teams">{informationSources.map((item, index) => <span key={item} data-group={index < 3 ? "market" : index < 6 ? "systems" : "experience"}>{item}</span>)}</div>
            <blockquote>The opportunity is not simply to generate more information. It is to make existing and new information work together around the commercial decision.</blockquote>
          </div>
        </section>

        <section className="about-section why-section" aria-labelledby="why-title">
          <div className="about-container why-layout">
            <Intro eyebrow="Why we are building Sales OS" title="Sales work should not restart at every stage." id="why-title"><p>A market-entry project can begin with research. Then come companies, people, outreach, conversations and new information.</p><p>Yet these stages are often handled by different tools and disconnected processes.</p><p>Apping Sales OS is being developed as a more continuous operating layer around that work.</p></Intro>
            <div className="why-sequences">
              <ol className="origin-sequence" aria-label="The perspective behind Apping Sales OS">
                <li>Executing practical business work</li>
                <li>Seeing where information breaks</li>
                <li>Connecting market, account and sales context</li>
                <li>Building Apping Sales OS</li>
              </ol>
              <ol className="about-workflow" aria-label="A connected sales workflow">{workflow.map((item, index) => <li key={item}><span>{String(index + 1).padStart(2, "0")}</span><strong>{item}</strong>{index < workflow.length - 1 ? <i aria-hidden="true"></i> : null}</li>)}</ol>
            </div>
          </div>
        </section>

        <section className="about-section people-section" aria-labelledby="people-title">
          <div className="about-container">
            <h2 id="people-title">The people behind Apping Technology.</h2>
            <div className="about-people-gallery">
              <figure><img src="/images/apping-team/apping-networking.jpg" srcSet="/images/apping-team/apping-networking-800.jpg 800w, /images/apping-team/apping-networking.jpg 1024w" sizes="(max-width: 820px) calc(100vw - 34px), 44vw" alt="Apping colleagues talking with attendees at a professional networking event" width="1024" height="768" loading="lazy" decoding="async" /><figcaption>Apping colleagues building connections</figcaption></figure>
              <figure><img src="/images/apping-team/apping-cipsprint.jpg" srcSet="/images/apping-team/apping-cipsprint-800.jpg 800w, /images/apping-team/apping-cipsprint.jpg 1024w" sizes="(max-width: 820px) calc(100vw - 34px), 44vw" alt="Apping colleagues and other attendees posing together at a CIPSprint event" width="1024" height="768" loading="lazy" decoding="async" /><figcaption>Apping colleagues at CIPSprint</figcaption></figure>
            </div>
            <div className="about-people-context"><p>Apping Technology builds practical business technology around a simple observation:</p><p>Market research, account knowledge, outreach and sales decisions become more useful when they are connected.</p><p>Apping Sales OS is a product being developed by Apping Technology around that principle.</p></div>
          </div>
        </section>

        <section className="about-section practical-section" aria-labelledby="practical-title">
          <div className="about-container practical-layout">
            <Intro eyebrow="Practical by design" title="Technology should help the work move forward." id="practical-title"><p>Apping’s product direction is grounded in practical business use.</p></Intro>
            <div className="question-field">{practicalQuestions.map((question, index) => <p key={question}><span>{index + 1}</span>{question}</p>)}</div>
            <blockquote>The value of intelligence is not the amount of information produced. It is whether the information helps the team take a better next action.</blockquote>
          </div>
        </section>

        <section className="about-section human-section" aria-labelledby="human-title">
          <div className="about-container human-layout">
            <Intro eyebrow="Human + AI" title="Use AI where it improves the work. Keep people responsible for the decisions that matter." id="human-title"><p>Sales OS is designed to use AI to support activities such as research, analysis, prioritization, preparation and working with accumulated context.</p><p>Human oversight remains important for consequential commercial decisions and actions.</p></Intro>
            <div className="responsibility-balance" aria-label="AI support and human responsibility">
              <div><span>AI supports</span><p>Research</p><p>Analysis</p><p>Prioritization</p><p>Preparation</p><p>Accumulated context</p></div>
              <i aria-hidden="true"></i>
              <div><span>People remain responsible</span><p>Consequential commercial decisions</p><p>Customer relationships</p><p>Actions and accountability</p></div>
            </div>
            <p className="about-support">More capable automation should create better operating leverage, not remove accountability.</p>
          </div>
        </section>

        <section className="about-section private-section" aria-labelledby="private-title">
          <div className="about-container private-layout">
            <div className="private-boundary" aria-label="Customer intelligence remains within that customer's private context"><span>Your company</span><div><strong>Private sales intelligence</strong><small>Interaction data</small><small>Account context</small><small>Useful learning</small></div><p>Not pooled into another customer’s intelligence layer</p></div>
            <Intro eyebrow="Customer context" title="What your company learns should strengthen your company." id="private-title"><p>A central principle behind Sales OS is that customer interaction data should contribute to that customer’s own private sales intelligence.</p><p>It is not intended to become another customer’s intelligence layer.</p><p className="about-support-inline">The longer-term product value comes from helping useful context remain available as opportunities develop.</p></Intro>
          </div>
        </section>

        <section className="about-section cross-border-section" aria-labelledby="cross-border-title">
          <div className="about-container cross-border-layout">
            <Intro eyebrow="Built with market expansion in mind" title="Unfamiliar markets make context more important." id="cross-border-title"><p>Entering a new country or territory creates uncertainty around the structure of the market and what the team should do first.</p><p>That makes international B2B expansion a natural starting use case for Sales OS.</p><p className="about-support-inline">The product is designed more broadly around complex B2B sales, not around one industry or one geography.</p></Intro>
            <figure className="about-market-environment"><img src="/images/editorial/southeast-asia-business-district.webp" srcSet="/images/editorial/southeast-asia-business-district-800.webp 800w, /images/editorial/southeast-asia-business-district.webp 1600w" sizes="(max-width: 760px) 100vw, 46vw" alt="Singapore central business district viewed across Marina Bay at dusk" width="1600" height="1067" loading="lazy" decoding="async" /><figcaption>Illustrative regional business environment. No customer or partnership relationship is implied.</figcaption></figure>
            <ul className="uncertainty-field">{uncertainties.map((item, index) => <li key={item}><span>{String(index + 1).padStart(2, "0")}</span>{item}</li>)}</ul>
          </div>
        </section>

        <section className="about-section principles-section" aria-labelledby="principles-title">
          <div className="about-container">
            <Intro title="The principles behind the product." id="principles-title" />
            <ol className="principles-list">{principles.map(([title, body], index) => <li key={title}><span>{String(index + 1).padStart(2, "0")}</span><h3>{title}</h3><p>{body}</p></li>)}</ol>
          </div>
        </section>

        <section className="about-section direction-section" aria-labelledby="direction-title">
          <div className="about-container direction-layout">
            <Intro title="Focused on the sales intelligence problem." id="direction-title"><p>Apping Sales OS represents Apping Technology’s current product direction around connecting the commercial context a B2B team needs.</p></Intro>
            <div className="direction-map" aria-label="Areas connected by Apping Sales OS"><strong>Apping<br />Sales OS</strong>{directions.map((item) => <span key={item}>{item}</span>)}</div>
          </div>
        </section>

        <section className="about-section building-section" aria-labelledby="building-title">
          <div className="about-container building-layout">
            <Intro eyebrow="Building the product" title="Start with the real workflow. Improve the system around it." id="building-title"><p>Sales OS is being developed around practical B2B sales problems rather than technology demonstrations.</p><p>The product direction is to connect increasingly more of the workflow while keeping the customer’s commercial objective and accumulated account context at the center.</p></Intro>
            <p className="building-statement">Progressive capability.<br /><strong>One connected operating model.</strong></p>
          </div>
        </section>

        <section className="about-section trust-section" aria-labelledby="trust-title">
          <div className="about-container trust-layout">
            <Intro eyebrow="Apping Technology" title="The company behind Sales OS." id="trust-title"><p>Apping Technology builds practical business technology. Apping Sales OS is its flagship product being developed around market intelligence, sales execution and accumulated account intelligence.</p></Intro>
            <dl className="company-facts">
              <div><dt>Company</dt><dd>Apping Technology</dd></div>
              <div><dt>Product</dt><dd>Apping Sales OS</dd></div>
              <div><dt>Official website</dt><dd><a href="https://appingtechnology.com">appingtechnology.com</a></dd></div>
              <div><dt>Product enquiries</dt><dd><a href="mailto:info@appingtechnology.com">info@appingtechnology.com</a></dd></div>
            </dl>
          </div>
        </section>

        <section className="about-section explore-section" aria-labelledby="explore-title">
          <div className="about-container">
            <Intro title="See how Sales OS approaches the sales problem." id="explore-title" />
            <nav className="explore-paths" aria-label="Explore Apping Sales OS">{explore.map(([title, body, href], index) => <a href={href} key={title}><span>{String(index + 1).padStart(2, "0")}</span><h3>{title}</h3><p>{body}</p><b aria-hidden="true">↗</b></a>)}</nav>
          </div>
        </section>

        <section className="about-section about-final" aria-labelledby="final-title">
          <div className="about-container about-final-inner">
            <p className="about-eyebrow">Start with your sales problem</p>
            <h2 id="final-title">Tell us where your team is trying to grow.</h2>
            <p>Bring us the market, account or sales-development problem your team is working on. We’ll use the walkthrough to explore whether Sales OS is relevant.</p>
            <ArrowLink href={WALKTHROUGH} primary>Book a walkthrough</ArrowLink>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
